import {
  concatUrl,
  createProxyUrl,
  DEFAULT_LOCALE,
  getCountry,
} from "../utils";
import { AemResponse, AemStruct } from "./AEMTypes";

import { AxiosResponse } from "axios";
import {
  RankedPersonaResponse,
  RankedPersonaValue,
} from "../components/AppProvider/useRankedPersonas";
import { getWithRetry } from "./ApiClient";

interface GetPageContentProps {
  page: string;
  aemBaseUrl: string;
  rankedPersonasResponse: Record<string, RankedPersonaResponse>;
  locale?: string;
}

type GetUnauthPageContentProps = Omit<
  GetPageContentProps,
  "rankedPersonasResponse"
>;

interface GetPageContentResult {
  page: string;
  response: Partial<AemStruct>;
}

const CONTENT_ROOT = "/content/dam/fordpro";

const buildContentRoot = (locale: string) =>
  `${CONTENT_ROOT}/${getCountry(locale)}/${locale}`;

const getUserPersonas = () =>
  window!.env!.USER.permissions!.filter((permission) =>
    permission.endsWith("_PERSONA"),
  );

const getUserEntitlements = () =>
  window!.env!.USER.permissions!.filter((permission) =>
    permission.endsWith("_ENT_PERM"),
  );

const callAEM = async (page: string, aemUrl: string) => {
  try {
    const axiosResponse: AxiosResponse = await getWithRetry(aemUrl, {
      method: "GET",
    });
    const aemResponse: AemResponse = axiosResponse?.data as AemResponse;
    const response: AemStruct = aemResponse.data.componentRefs;
    return { page, response: response };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

const getUnauthenticatedPageContent = async (
  args: GetUnauthPageContentProps,
) => {
  const { page, aemBaseUrl, locale = DEFAULT_LOCALE } = args;
  const path = `${buildContentRoot(locale)}/${page}/endpoint.pro-model.json`;

  const aemUrl = concatUrl(aemBaseUrl, path);

  return await callAEM(page, aemUrl);
};

const getAuthenticatedPageContent = async (args: GetPageContentProps) => {
  const {
    page,
    aemBaseUrl,
    rankedPersonasResponse,
    locale = DEFAULT_LOCALE,
  } = args;

  const { personaRanking: pagePersonaRanking, entitlements: pageEntitlements } =
    rankedPersonasResponse[page]!;

  const userPersonas = getUserPersonas();
  if (userPersonas.length === 0) {
    console.error("No persona found");
    throw Error("No persona found");
  }

  const highestRankingUserPersona =
    pagePersonaRanking?.find((x) => userPersonas.includes(x)) ??
    userPersonas[0];

  const effectiveEntitlements = getUserEntitlements().filter(
    (userEntitlement) => pageEntitlements?.includes(userEntitlement),
  );

  const ranking = [...effectiveEntitlements, highestRankingUserPersona].join(
    ".",
  );
  const path = `${buildContentRoot(
    locale,
  )}/tools/${page}/endpoint.pro-model.${ranking}.json`;

  const aemProxyUrl = createProxyUrl(concatUrl(aemBaseUrl, path));
  return await callAEM(page, aemProxyUrl);
};

const getRankedPersonas = async (
  aemBaseUrl: string,
  locale = DEFAULT_LOCALE,
): Promise<RankedPersonaValue> => {
  const url = createProxyUrl(
    concatUrl(
      aemBaseUrl,
      `${buildContentRoot(locale)}/tools/permissions/permissions.json`,
    ),
  );

  const response = await getWithRetry<RankedPersonaValue, any>(url, {
    timeout: 20000,
    shouldRetryWhenTimeout: true,
    retryWait: [2000],
    errorsToRetry: [401, 403],
    method: "get",
  })
  return response.data;
};

export {
  getAuthenticatedPageContent,
  getRankedPersonas,
  getUnauthenticatedPageContent,
};
export type {
  GetPageContentProps,
  GetPageContentResult,
  GetUnauthPageContentProps,
};
